import { FC } from 'react';
import { IoGlobeOutline } from 'react-icons/io5';

import { Box, Group, Text } from '@mantine/core';

import { Component } from 'models';
import { ComponentHelpers } from 'helpers/ComponentHelpers';

import { CompanyLogo } from 'components/company-logo/CompanyLogo';
import { ProfileCard } from 'components/company-profile/components/ProfileCard';
import { ComponentIcon } from 'components/component-icons/ComponentIcon';
import { ComponentOverviewHitAveragePrice } from 'components/component-overview/ComponentOverviewHitAveragePrice';
import { ComponentOverviewHitDistributorPrice } from 'components/component-overview/ComponentOverviewHitDistributorPrice';
import { ComponentThumbnail } from 'components/thumbnail/ComponentThumbnail';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useComponentMeta } from 'hooks/use-component-meta';

import type { ComponentPricing } from '@/payload-types';

import cx from './ProductTeaser.module.scss';

const ProductTeaser: FC<{
    product: Component;
    distributorPrice?: ComponentPricing | null;
}> = ({ product, distributorPrice }) => {
    const { uniqueName, typeName, slug, regionAvailability } = useComponentMeta(product);
    const { company: manufacturer } = useCompanyProfile(product.manufacturer);

    const hasImage = product.images?.some((image) => image.file);
    const typeLabel = <Text className={cx.placeholderType}>{typeName}</Text>;

    return (
        <ProfileCard
            href={ComponentHelpers.urls.view(product.id, slug)}
            imageFit="contain"
            image={
                hasImage ? (
                    <ComponentThumbnail component={product} />
                ) : (
                    <Box className={cx.placeholder}>
                        {manufacturer ? (
                            <CompanyLogo logos={manufacturer.logos} width={400} fallback={typeLabel} />
                        ) : (
                            typeLabel
                        )}
                    </Box>
                )
            }
            eyebrow={typeName}
            eyebrowIcon={<ComponentIcon type={product.type} />}
            title={uniqueName}
            meta={
                <>
                    {distributorPrice && <ComponentOverviewHitDistributorPrice distributorPrice={distributorPrice} />}
                    <ComponentOverviewHitAveragePrice component={product} />
                    {regionAvailability && (
                        <Group gap={4} wrap="nowrap">
                            <IoGlobeOutline size={12} />
                            <Text inherit truncate>
                                {regionAvailability}
                            </Text>
                        </Group>
                    )}
                </>
            }
            description={product.description}
            cta="View product"
        />
    );
};

export { ProductTeaser };
